/* eslint-disable @typescript-eslint/ban-ts-comment */
import * as R from 'ramda'
import {
  memo,
  useCallback,
  useEffect,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import { styled } from '@mui/material'
import { useInView } from 'react-intersection-observer'
import { useGesture } from '@use-gesture/react'
import { animated, useSpring } from '@react-spring/web'
import {
  Message,
  MessageSender,
  RawEventMessage,
} from '@weblite/ts-action/message'
import { RoomTypes } from '@weblite/ts-action/room'
import {
  AQUA_LIST_ITEM_CLASS,
  generateMessageId,
  useAquaManagerId,
} from '@weblite/aqua'
import { useLongPress } from '$hooks/use-long-press'
import { PendingUpload } from '$logic/pending-uploads/types'
import { PendingMessage } from '$logic/pending-messages/types'
import {
  handleViewCount,
  isMessageFromClientUser,
  ViewCountableMessage,
} from '$logic/message'
import { setSeen } from '$logic/room-seen'
import { sendFieldApis } from '$comp/_app/components/chat/components/footer/components/send-field'
import { isPhoneOrTablet, isTouchAvailable } from '$func/environmentInfo'
import { vibrate } from '$func/vibrate'
import { useTimeout } from '$hooks/use-timeout'
import { useRoomPendingEditUploadByIid } from '$logic/pending-edit-uploads/react-hook'
import { useRoomPendingEditByIid } from '$logic/pending-edits/react-hook'
import { showForwardMenuModal } from '$comp/modals/forward-menu'
import {
  changeMessageVisibilityInViewPort,
  changePinVisibilityInViewPort,
  isMessageVisible,
} from '$logic/pin'
import { usePrevious } from '$common/react-hooks/use-previous'
import { MessageSelector } from '../../messages'
import { Event } from '../event/event'
import { ForwardButton } from '../../messages/components/forward-button'
import { ProfilePicture } from '../../messages/components/profile-picture'
import { SelectionContext } from '../../../../../../room'
import { useHandlers, useShouldObserveMessages } from './react-hooks'
import { PosInBlock } from '../../react-hooks'
import {
  DRAG_OFFSET_LIMIT,
  DRAG_THRESHOLD,
  REPLY_THRESHOLD,
} from './message-wrapper.contant'

const GET_VIDEO_THUMBNAIL_TIMEOUT = 2000
export function getPadding(
  posInBlock: PosInBlock,
  hasBigSpace: boolean,
  isChannel: boolean,
  hasSideSpace = true,
): string {
  const paddingUnit = hasBigSpace ? 10 : 6
  const paddingSmallUnit = hasBigSpace ? 6 : 3

  const paddingTop =
    posInBlock === 'SINGLE' || posInBlock === 'START' || isChannel
      ? paddingUnit
      : paddingSmallUnit

  const paddingBottom =
    posInBlock === 'SINGLE' || posInBlock === 'END' || isChannel
      ? paddingUnit
      : paddingSmallUnit

  return `${paddingTop}px ${hasSideSpace ? 8 : 0}px ${paddingBottom}px`
}

interface StyledListItemProps {
  posInBlock: PosInBlock
  hasBigSpace: boolean
  isChannel: boolean
  selected: boolean
}

const StyledListItem = styled('li', {
  shouldForwardProp: prop =>
    !['posInBlock', 'hasBigSpace', 'isChannel', 'selected'].includes(
      prop as string,
    ),
})<StyledListItemProps>(({ posInBlock, hasBigSpace, isChannel, selected }) => ({
  display: 'flex',
  margin: 0,
  padding: getPadding(posInBlock, hasBigSpace, isChannel),
  '&:first-of-type': {
    marginTop: 6,
  },
  '&:last-child': {
    marginBottom: 6,
  },

  background: 'transparent',
  transition: 'background 200ms',
  '&[data-highlight]': {
    background: '#55A6FF4D !important',
  },

  'div[data-needs-selection=true]': {
    userSelect: !isPhoneOrTablet || selected ? 'text' : 'inherit',
  },
}))

interface MessageDecoratorProps {
  fromMe: boolean
}

const MessageDragDecorator = styled(animated.div)`
  width: 100%;
  display: flex;
`

const MessageDecorator = styled('div', {
  shouldForwardProp: prop => prop !== 'fromMe',
})<MessageDecoratorProps>`
  display: flex;
  /* @noflip */
  margin-right: ${({ fromMe }) => (fromMe ? 'unset' : 'auto')};
`

interface MessageSelectorDecoratorProps {
  posInBlock: PosInBlock
  fromMe: boolean
  isChannel: boolean
  renderAvatarGap: boolean
}

const MessageBodyDecorator = styled('div', {
  shouldForwardProp: prop =>
    !['posInBlock', 'fromMe', 'isChannel', 'renderAvatarGap'].includes(
      prop as string,
    ),
})<MessageSelectorDecoratorProps>(
  ({ posInBlock, fromMe, isChannel, renderAvatarGap }) => `
    width: fit-content;
    max-width: 280px;
    overflow: hidden;
    /* @noflip */
    margin-left: ${renderAvatarGap ? '52px' : '0'};
    background-color: ${fromMe ? '#F0F7FF' : '#FFF'};
    /* @noflip */
     border-radius: ${
       isChannel
         ? `16px 16px ${fromMe ? '0 16px' : '16px 0'}`
         : posInBlock === 'START'
           ? `16px 16px ${fromMe ? '8px 16px' : '16px 8px'}`
           : posInBlock === 'MID'
             ? fromMe
               ? '16px 8px 8px 16px'
               : '8px 16px 16px 8px'
             : fromMe
               ? '16px 8px 0 16px'
               : '8px 16px 16px 0'
     };
    box-shadow: 0px 0px 4px 0px #0000001A;
   `,
)
type MessageTypes =
  | Message
  | PendingUpload
  | PendingMessage
  | ViewCountableMessage
export function MessageWrapperComponent({
  message: rawMessage,
  posInBlock,
  showSender,
  needVisibility,
  toggleSelection,
  roomType,
  hasPermissionToSendMessages,
  disableIntersection = false,
}: {
  message: MessageTypes
  posInBlock: PosInBlock
  showSender: boolean
  needVisibility?: boolean
  toggleSelection: (selected: boolean) => (iid: number) => unknown
  roomType: RoomTypes
  hasPermissionToSendMessages: boolean
  disableIntersection?: boolean
}) {
  // @ts-ignore
  const { id, iid, type, sender, roomId } = rawMessage
  const messagePendingEditUpload = useRoomPendingEditUploadByIid(roomId, iid)
  const aquaManagerId = useAquaManagerId()
  const messagePendingEdit = useRoomPendingEditByIid(roomId, iid)
  const pendingEdit = messagePendingEdit || messagePendingEditUpload

  const {
    create: createGetHighQualityVideoThumbnailTimeout,
    clear: clearGetHighQualityVideoThumbnailTimeout,
  } = useTimeout()

  const message = useMemo(
    () =>
      ({
        ...rawMessage,
        ...(pendingEdit
          ? {
              attachment: pendingEdit.attachment || rawMessage.attachment,
              uploadId: messagePendingEditUpload?.uploadId,
              body:
                pendingEdit.body !== undefined
                  ? pendingEdit.body
                  : rawMessage.body,
              thumbnailUploadId: pendingEdit?.thumbnailUploadId,
            }
          : {}),
      }) as MessageTypes,
    [rawMessage, pendingEdit, messagePendingEditUpload],
  )

  const selectionMap = useContext(SelectionContext)

  // @ts-ignore
  const selected = selectionMap[iid]
  const isSelectionMode = R.keys(selectionMap).length > 0

  const handlers = useHandlers(
    roomId,
    id,
    iid,
    type as Message['type'],
    selected,
    isSelectionMode,
    toggleSelection(selected),
  )

  const longPressHandlers = useLongPress(handlers.onLongPress)

  const fromMe = isMessageFromClientUser(message as Message)

  // NOTE: If sender was the admin of channel, should show PENDING status
  const canShowStatus = fromMe || !iid
  const isChannel = roomType === 'CHANNEL'

  const canShowProfilePicture =
    !fromMe && (roomType === 'GROUP' || roomType === 'PRIVATE_SPACE')

  const renderProfilePicture =
    canShowProfilePicture && (posInBlock === 'SINGLE' || posInBlock === 'END')
  const renderAvatarGap =
    canShowProfilePicture && (posInBlock === 'START' || posInBlock === 'MID')

  const canShowSenderName = showSender && !fromMe && roomType === 'GROUP'

  const [
    shouldGetVideoHighQualityThumbnail,
    setShouldGetVideoHighQualityThumbnail,
  ] = useState(false)

  const [shouldHandleViewCount, setShouldHandleViewCount] = useState(
    Boolean(iid),
  )
  const prevIsPinned = usePrevious(message.pinned)

  useEffect(() => {
    return () => {
      if (disableIntersection) return
      changePinVisibilityInViewPort(roomId, iid, false)
      changeMessageVisibilityInViewPort(roomId, iid, false)
    }
  }, [disableIntersection])

  useEffect(() => {
    if (disableIntersection) return
    if (!prevIsPinned && message.pinned && isMessageVisible(roomId, iid)) {
      changePinVisibilityInViewPort(roomId, iid, true)
    }
    if (prevIsPinned && !message.pinned) {
      changePinVisibilityInViewPort(roomId, iid, false)
    }
  }, [message.pinned, disableIntersection])

  const handleViewCountOnVisibilityChange = useCallback(
    (inView: boolean) => {
      if (inView && shouldHandleViewCount) {
        handleViewCount(message as ViewCountableMessage)
        if (roomType !== 'PRIVATE_SPACE') setSeen(roomId, iid, fromMe)
        setShouldHandleViewCount(false)
      }
    },
    [shouldHandleViewCount],
  )

  const handleVideoHighQualityThumbnailOnVisibilityChange = useCallback(
    (inView: boolean) => {
      if (inView) {
        createGetHighQualityVideoThumbnailTimeout(() => {
          setShouldGetVideoHighQualityThumbnail(true)
        }, GET_VIDEO_THUMBNAIL_TIMEOUT)
      } else {
        clearGetHighQualityVideoThumbnailTimeout()
      }
    },
    [],
  )
  const handlePinAndMessageVisibilityOnVisibilityChange = useCallback(
    (inView: boolean) => {
      if (disableIntersection) return
      if (message.pinned) changePinVisibilityInViewPort(roomId, iid, inView)
      changeMessageVisibilityInViewPort(roomId, iid, inView)
    },
    [message, disableIntersection],
  )

  const handleVisibilityChange = useCallback(
    (inView: boolean) => {
      handlePinAndMessageVisibilityOnVisibilityChange(inView)
      handleViewCountOnVisibilityChange(inView)
      handleVideoHighQualityThumbnailOnVisibilityChange(inView)
    },
    [
      handlePinAndMessageVisibilityOnVisibilityChange,
      handleViewCountOnVisibilityChange,
      handleVideoHighQualityThumbnailOnVisibilityChange,
    ],
  )

  const shouldObserveMessages = useShouldObserveMessages()

  const { ref: observerRef } = useInView({
    skip: !shouldObserveMessages,
    onChange: handleVisibilityChange,
  })

  const [{ x: xOffset }, api] = useSpring(() => ({ x: 0 }))
  const isVibrated = useRef<boolean>(false)

  const gestureBind = useGesture(
    {
      onDrag: ({ down, movement: [mx] }) => {
        api.start({
          x: down ? Math.max(-DRAG_OFFSET_LIMIT, Math.min(mx, 0)) : 0,
          immediate: down,
        })
        if (mx < -REPLY_THRESHOLD) {
          if (isVibrated.current) return
          vibrate()
          isVibrated.current = true
        }
      },
      onDragEnd: ({ movement: [mx] }) => {
        if (mx < -REPLY_THRESHOLD) {
          setTimeout(() => sendFieldApis.setReplyContent(iid), 0)
          isVibrated.current = false
        }
      },
    },
    {
      drag: {
        threshold: DRAG_THRESHOLD,
        enabled:
          hasPermissionToSendMessages && isTouchAvailable && !isSelectionMode,
      },
    },
  )

  return (
    <StyledListItem
      dir="rtl"
      id={generateMessageId(aquaManagerId, id)}
      data-iid={iid}
      key={id}
      className={AQUA_LIST_ITEM_CLASS}
      tabIndex={0}
      data-need-visibility={needVisibility}
      style={{ backgroundColor: selected ? '#55A6FF4D' : 'unset' }}
      hasBigSpace={type === 'EVENT'}
      posInBlock={posInBlock}
      isChannel={isChannel}
      selected={selected}
      {...handlers}
      {...longPressHandlers}
    >
      {type === 'EVENT' ? (
        <Event
          ref={observerRef}
          message={message as RawEventMessage}
          roomId={roomId}
        />
      ) : (
        <MessageDragDecorator
          {...gestureBind()}
          style={{
            x: xOffset,
            touchAction: 'pan-y',
            pointerEvents: selected ? 'none' : 'initial',
          }}
          id={iid}
        >
          <MessageDecorator fromMe={!isChannel && fromMe}>
            {isChannel && (
              <ForwardButton
                roomId={roomId}
                onClick={() => showForwardMenuModal(roomId, [iid])}
              />
            )}

            <MessageBodyDecorator
              isChannel={isChannel}
              posInBlock={posInBlock}
              fromMe={!isChannel && fromMe}
              renderAvatarGap={renderAvatarGap}
              ref={observerRef}
            >
              <MessageSelector
                shouldGetVideoHighQualityThumbnail={
                  shouldGetVideoHighQualityThumbnail
                }
                message={message}
                canShowStatus={canShowStatus}
                canShowSenderName={canShowSenderName}
                isInSelectionMode={isSelectionMode}
              />
            </MessageBodyDecorator>

            {renderProfilePicture && (
              <ProfilePicture sender={sender as MessageSender} />
            )}
          </MessageDecorator>
        </MessageDragDecorator>
      )}
    </StyledListItem>
  )
}

MessageWrapperComponent.defaultProps = {
  needVisibility: '',
}

export const MessageWrapper = memo(
  MessageWrapperComponent,
  (oldProps, newProps) => R.equals(oldProps, newProps),
)
